import { ctcDynamicFieldRows, isCtcActive } from "../../../../shared/ctcMasterData";
import { ctcHidesBrokerCommission } from "../../../../shared/ctcAccess";
import { CtcDecisionEvidence, CtcDeliveryRecovery } from "./ctc_decisions";
import { Fragment, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Accordion, ActionIcon, Anchor, Badge, Button, Card, FileInput, Grid, Group, Loader, Modal, NumberInput, Paper, Select, SimpleGrid, Stack, Text, Textarea, TextInput, ThemeIcon, Timeline, Title, Tooltip } from "@mantine/core";
import { IconArrowLeft, IconBox, IconCheck, IconClock, IconFileDescription, IconFiles, IconHistory, IconMessageCircle, IconReceipt, IconBuildingBank, IconUser, IconX } from "@tabler/icons-react";
import { CtcInfoCard } from "./ctc_info_card";
import { notifications } from "@mantine/notifications";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { useStorage } from "../../helpers/firebaseContext";
import { TagBadge } from "../../components/tag_badge";
import { CtcHistory } from "./ctc_history";
import { useLiveCollection, useLiveDoc } from "./useLive";
import { CtcBroker, CtcCommission, CtcCommodity, CtcCustomer, CtcDynamicFieldDef, CtcPendingReply, CtcPurchaseOrder, CtcSettlement, CtcSettlementSide, CtcSettlementStatus } from "../../types";
import { CtcAction, CTC_PO_STATUS_TAGS, CTC_DEALER_UPLOADS, CTC_MAX_DEALER_UPLOAD_FILES, isTerminal } from "../../../../shared/ctcLifecycle";
import { CTC_ACTION_LABELS, ctcActionsFor, ctcStatusTag, formatMoney, formatValueDate, useCtcCallables, useCtcUser } from "./ctc_helpers";
import { useCtcDealers } from "./ctc_dealers";
import { CtcFilePicker } from "./ctc_file_picker";

/** kfh-capital-crm-test only: one user may make and check the same order (mirrors functions-ctc/src/common.ts). */

/** One CtcInfoCard row; empty values show "—". */
const info = (label: string, value?: string | number | null) => ({ label, value: value ?? undefined });

type Stamp = { toDate?: () => Date } | undefined;
const SOLD_METHOD: Record<string, string> = { whatsapp: "WhatsApp", email: "email", manual: "recorded by dealer", customer: "customer request", ctc: "CTC" };
const STOPPED: Partial<Record<string, { label: string; color: string }>> = {
    deleted: { label: "Deleted", color: "gray" },
    cancelled: { label: "Cancelled", color: "red" },
    cancelled_after_completion: { label: "Cancelled after completion", color: "orange" },
};

/**
 * The order's steps top to bottom: done steps filled with their time and who, the next ones grey.
 * A rejected / cancelled / deleted order ends with a red step and the reason.
 */
function OrderTimeline({ po }: { po: CtcPurchaseOrder }) {
    const code = po.tags?.poStatus?.code ?? "";
    const stopped = code.includes("rejected") ? { label: CTC_PO_STATUS_TAGS[code as keyof typeof CTC_PO_STATUS_TAGS]?.title ?? "Rejected", color: "red" } : STOPPED[code];
    const steps: { label: string; at: Stamp; by?: string }[] = [
        { label: "Created", at: po.created as Stamp, by: po.makerEmail },
        { label: "Submitted", at: po.submittedAt as Stamp, by: po.checkerEmail ?? po.makerEmail },
        { label: "Accepted", at: po.acceptedAt as Stamp, by: po.acceptedByEmail },
        { label: "Purchased", at: po.purchasedAt as Stamp, by: po.assignedDealerEmail },
        { label: "Transferred", at: po.transferredAt as Stamp, by: po.transferRequestedByEmail },
        { label: po.soldMethod ? `Sold (${SOLD_METHOD[po.soldMethod] ?? po.soldMethod})` : "Sold", at: po.soldAt as Stamp },
        { label: "Completed", at: po.completedAt as Stamp },
    ];
    const done = steps.filter((st) => st.at?.toDate);
    // Stopped orders show what happened, then the stop; open ones also show the steps still to come
    const shown = stopped ? done : steps;
    const active = shown.reduce((last, st, i) => (st.at?.toDate ? i : last), -1) + (stopped ? 1 : 0);
    return (
        <Paper shadow="xs" p="md">
            <Group gap="sm" mb="md">
                <ThemeIcon color="grape" variant="light"><IconClock size={18} /></ThemeIcon>
                <Text>Timeline</Text>
            </Group>
            <Timeline active={active} bulletSize={20} lineWidth={2} color="grape">
                {shown.map((st) => {
                    const isDone = !!st.at?.toDate;
                    return (
                        <Timeline.Item key={st.label} bullet={isDone ? <IconCheck size={12} /> : undefined}
                            title={<Text size="sm" fw={isDone ? 600 : 400} c={isDone ? undefined : "dimmed"}>{st.label}</Text>}>
                            {isDone ? (
                                <>
                                    <Text size="xs" c="dimmed">{when(st.at)}</Text>
                                    {st.by && <Text size="xs" c="dimmed">{st.by}</Text>}
                                </>
                            ) : <Text size="xs" c="dimmed">Pending</Text>}
                        </Timeline.Item>
                    );
                })}
                {stopped && (
                    <Timeline.Item color={stopped.color} bullet={<IconX size={12} />} title={<Text size="sm" fw={600} c={stopped.color}>{stopped.label}</Text>}>
                        <Text size="xs" c="dimmed">{when((po.cancelledAt ?? po.lastAction?.at ?? po.updated) as Stamp)}</Text>
                        {(po.cancelledByEmail ?? po.lastAction?.by) && <Text size="xs" c="dimmed">{po.cancelledByEmail ?? po.lastAction?.by}</Text>}
                        {(po.cancelReason || po.rejectionReason) && <Text size="xs">{po.cancelReason ?? po.rejectionReason}</Text>}
                    </Timeline.Item>
                )}
            </Timeline>
        </Paper>
    );
}

const SETTLEMENT_STATUS: Record<CtcSettlementStatus, { label: string; color: string }> = {
    settled: { label: "Settled", color: "green" },
    unsettled: { label: "Unsettled", color: "orange" },
    pending_waive_approval: { label: "Waiver pending", color: "yellow" },
    waived: { label: "Waived", color: "gray" },
};
/** Settlement of one side: status badge, with reference / comment and when / by underneath. */
const settlementRow = (label: string, side?: CtcSettlementSide) => {
    if (!side) return { label, value: "When the order completes" }; // settlements open at completion
    const st = SETTLEMENT_STATUS[side.status] ?? { label: side.status, color: "gray" };
    const details = [
        side.reference ? `Ref ${side.reference}` : side.comment,
        side.settledAt ? `${when(side.settledAt)}${side.settledBy ? ` by ${side.settledBy}` : ""}` : undefined,
    ].filter(Boolean).join(" · ");
    return {
        label: side.amount !== undefined ? `${label} (${formatMoney(side.amount, side.currencyId)})` : label,
        sublabel: details || undefined,
        value: <Badge variant="light" color={st.color}>{st.label}</Badge>,
    };
};

/** Buttons the order page doesn't show (the lifecycle still allows them, e.g. for admins via the API). */
const HIDDEN_ACTIONS: ReadonlySet<CtcAction> = new Set([CtcAction.reject_transfer, CtcAction.reject_transfer_by_ctc, CtcAction.beneficiary_reject_sell]);

/** Accordion header in the fund order page's style: gradient icon + title. */
const SectionTitle = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
    <Group gap="md" wrap="nowrap">
        <ThemeIcon variant="gradient" size="xl" gradient={{ from: "red", to: "grape", deg: 180 }}>{icon}</ThemeIcon>
        <Text component="span" variant="gradient" gradient={{ from: "red", to: "grape", deg: 180 }} size="xl" fw={700}>{title}</Text>
    </Group>
);

const when = (ts?: { toDate?: () => Date }) => ts?.toDate?.().toLocaleString("en-GB", { hour12: true });

/** Purchase order detail: facts, lifecycle action bar (driven by the shared state machine), history. */
const CtcOrder = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const user = useCtcUser();
    const { transition, retryEffects } = useCtcCallables();
    const storage = useStorage();
    const [retrying, setRetrying] = useState(false);

    // Everything on this page is a live subscription keyed by path, so it follows the order
    // that is open and updates as the lifecycle, automation and WhatsApp answers land.
    const { data: po, loading, exists } = useLiveDoc<CtcPurchaseOrder>(id ? `ctc_purchase_orders/${id}` : null);
    // Created when the purchase is confirmed (doc id = order id); settled in Transactions
    const { data: settlement } = useLiveDoc<CtcSettlement>(id ? `ctc_settlements/${id}` : null);
    // Orders saved before dynamic field labels were copied onto them: label with the customer's current fields
    const needsFieldLabels = !!po && !po.dynamicFieldDefs && Object.keys(po.dynamicFields ?? {}).length > 0;
    const { data: customerFields } = useLiveCollection<CtcDynamicFieldDef>(needsFieldLabels ? `ctc_customers/${po!.customerId}/dynamic_fields` : null);
    const currentFieldLabels = needsFieldLabels ? Object.fromEntries((customerFields ?? []).map((f) => [f.id!, f])) : undefined;
    const { data: customerData } = useLiveDoc<CtcCustomer>(po?.customerId ? `ctc_customers/${po.customerId}` : null);
    const customer = useMemo(() => (customerData ?? {}) as CtcCustomer, [customerData]);

    const { dealers } = useCtcDealers();
    // A dealer from the dealer list can take the order themselves when accepting (server checks the same claims)
    const canAssignSelf = user.has("dealer") && user.isDesk && dealers.some((d) => d.email === user.email);
    const dealerOptions = dealers.map((d) => ({
        value: d.email,
        label: `${d.displayName || d.email}${d.displayName ? ` (${d.email})` : ""}${d.email === po?.assignedDealerEmail ? " · current" : ""}`,
    }));

    // desk-only collection (rules): KFH users never subscribe to it
    const { data: pendingReply } = useLiveDoc<CtcPendingReply>(user.isDesk && po?.beneficiary?.whatsapp ? `ctc_pending_replies/${po.beneficiary.whatsapp}` : null);
    const pendingForThisPo = user.isDesk && pendingReply && pendingReply.poId === id ? pendingReply : undefined;
    const brokers = useLiveCollection<CtcBroker>("ctc_brokers").data;
    const commodities = useLiveCollection<CtcCommodity>("ctc_commodities").data;
    const commissions = useLiveCollection<CtcCommission>("ctc_commissions").data;

    const [openSections, setOpenSections] = useState<string[]>(["details"]); // History / evidence open on demand
    const [pending, setPending] = useState<CtcAction | null>(null);
    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState("");
    const [dealerEmail, setDealerEmail] = useState("");
    const [documentFiles, setDocumentFiles] = useState<Record<string, File[]>>({});
    const [purchase, setPurchase] = useState<{ brokerId: string | null; commodityId: string | null; quantity: number | string; unitPrice: number | string; locationId: string | null; locationName: string; certificateNumber: string }>({
        brokerId: null, commodityId: null, quantity: "", unitPrice: "", locationId: null, locationName: "", certificateNumber: "",
    });

    // Maker/checker only for customers in the user's Microsoft groups, and the server's own restrictions
    // Not offered here (business decision): rejecting a transfer (checker or CTC) and recording a beneficiary's refusal to sell
    const hideBroker = ctcHidesBrokerCommission(user);
    const actions = useMemo(() => (po ? ctcActionsFor(po, user, customer).filter((a) => !HIDDEN_ACTIONS.has(a)) : [] as CtcAction[]), [po, user, customer]);

    if (!loading && !exists) return <Text c="dimmed">Purchase order not found (or not visible to you).</Text>;
    if (loading || !po) return <Loader />;

    const needsReason = pending ? CTC_ACTION_LABELS[pending].needsReason : false;
    const needsDealer = pending === CtcAction.accept || pending === CtcAction.reassign;
    const needsPurchase = pending === CtcAction.confirm_purchase;
    const uploadStep = pending ? CTC_DEALER_UPLOADS[pending] : undefined;
    const missingUpload = !!uploadStep?.documents.some((d) => d.required && !documentFiles[d.kind]?.length);

    // Purchase defaults: unit price from the commodity's indicative price, quantity = order amount / unit price.
    const quantityFor = (price: number | string) => {
        const p = Number(price);
        return p > 0 ? Math.round((po.amount / p) * 1e12) / 1e12 : ""; // up to 12 decimals, like the input
    };
    const selectedBroker = (brokers ?? []).find((b) => b.id === purchase.brokerId);
    const brokerLocations = (selectedBroker?.locations ?? []).map((l, i) => ({ value: l.id || `loc-${i}`, label: l.name_en, name: l.name_en }));
    const selectedCommodity = (commodities ?? []).find((c) => c.id === purchase.commodityId);
    const commodityCurrencyMismatch = !!selectedCommodity?.currencyId && selectedCommodity.currencyId !== po.currencyId;
    const purchaseTotal = Number(purchase.quantity) * Number(purchase.unitPrice);

    const pickBroker = (brokerId: string | null) => {
        const locations = (brokers ?? []).find((b) => b.id === brokerId)?.locations ?? [];
        const only = locations.length === 1 ? locations[0] : undefined; // one location: select it
        setPurchase({ ...purchase, brokerId, locationId: only ? (only.id || "loc-0") : null, locationName: only?.name_en ?? "" });
    };
    const pickCommodity = (commodityId: string | null) => {
        const price = (commodities ?? []).find((c) => c.id === commodityId)?.price;
        setPurchase({ ...purchase, commodityId, unitPrice: price ?? "", quantity: price ? quantityFor(price) : "" });
    };

    const closeModal = () => {
        setPending(null);
        setDocumentFiles({});
        setPurchase({ brokerId: null, commodityId: null, quantity: "", unitPrice: "", locationId: null, locationName: "", certificateNumber: "" });
    };

    const run = async () => {
        if (!pending || !id) return;
        setBusy(true);
        try {
            const documents: { kind: string; path: string }[] = [];
            if (uploadStep) {
                for (const doc of uploadStep.documents) {
                    for (const [i, file] of (documentFiles[doc.kind] ?? []).entries()) {
                        // Canonical extension from the accepted type (handles .jfif, no extension, upper case).
                        const ext = ({ "application/pdf": "pdf", "image/jpeg": "jpg", "image/jpg": "jpg", "image/png": "png" } as Record<string, string>)[file.type];
                        if (!ext) throw new Error(`${file.name}: only PDF, JPG or PNG files are allowed`);
                        const path = `ctc/${id}/dealer_${uploadStep.number}_${doc.kind}_${Date.now()}_${i}.${ext}`;
                        await uploadBytes(storageRef(storage, path), file, {
                            contentType: file.type || "application/pdf",
                            customMetadata: {
                                poId: id,
                                number: String(uploadStep.number),
                                kind: doc.kind,
                                label: doc.label,
                                originalName: file.name,
                                uploadedBy: user.email,
                            },
                        });
                        documents.push({ kind: doc.kind, path });
                    }
                }
            }
            const result = await transition({
                poId: id,
                action: pending,
                reason: needsReason ? reason : undefined,
                dealerEmail: needsDealer ? dealerEmail : undefined,
                purchase: needsPurchase ? {
                    brokerId: purchase.brokerId ?? "",
                    commodityId: purchase.commodityId ?? "",
                    quantity: Number(purchase.quantity),
                    unitPrice: Number(purchase.unitPrice),
                    locationId: purchase.locationId || undefined,
                    locationName: purchase.locationName || undefined,
                    certificateNumber: purchase.certificateNumber.trim(),
                } : undefined,
                documents: uploadStep ? documents : undefined,
            });
            notifications.show({ color: "green", message: CTC_PO_STATUS_TAGS[result.data.status].title });
            if (result.data.pendingEffects?.length) {
                notifications.show({ color: "orange", message: `Not yet automated: ${result.data.pendingEffects.join(", ")}` });
            }
            closeModal();
            setReason("");
            if (pending === CtcAction.delete) navigate("/ctc/orders");
        } catch (e) {
            console.error("ctcTransition failed", e);
            notifications.show({ color: "red", message: (e as Error).message || "Action failed" });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Group justify="space-between" mb="md">
                <Group>
                    {/* Back to the list it was opened from (same filters and page); a direct link goes to Purchase orders */}
                    <Tooltip label="Back">
                        <ActionIcon variant="filled" size="lg" radius="md" aria-label="Back"
                            onClick={() => ((window.history.state as { idx?: number } | null)?.idx ? navigate(-1) : navigate("/ctc/orders"))}>
                            <IconArrowLeft size={20} />
                        </ActionIcon>
                    </Tooltip>
                    <Title order={3}>{po.referenceNumber}</Title>
                    <TagBadge tag={ctcStatusTag(po.tags.poStatus)} />
                    {po.lane && <Badge variant="light" color={po.lane === "beneficiary" ? "green" : "gray"}>waiting on {po.lane}</Badge>}
                </Group>
                <Group>
                    {po.tags.poStatus.code === "draft" && (user.has("maker") || user.isAdmin) && (
                        <Button variant="default" component={Link} to={`/ctc/orders/${id}/edit`}>Edit draft</Button>
                    )}
                    {actions.map((action) => (
                        <Fragment key={action}>
                            <Button color={CTC_ACTION_LABELS[action].color} variant={action.startsWith("reject") || action === CtcAction.delete ? "light" : "filled"} onClick={() => { setDealerEmail(""); setPending(action); }}>
                                {CTC_ACTION_LABELS[action].label}
                            </Button>
                            {action === CtcAction.accept && canAssignSelf && (
                                <Button color={CTC_ACTION_LABELS[action].color} variant="light" onClick={() => { setDealerEmail(user.email); setPending(action); }}>
                                    Accept & assign me
                                </Button>
                            )}
                        </Fragment>
                    ))}
                    {actions.length === 0 && !isTerminal(po.tags.poStatus.code) && <Text size="sm" c="dimmed">No actions available for your role</Text>}
                </Group>
            </Group>

            {po.pendingEffects?.length ? (
                <Card withBorder mb="md" style={{ borderColor: "var(--mantine-color-orange-5)" }}>
                    <Group justify="space-between" align="flex-start">
                        <Stack gap={4}>
                            <Text fw={700} c="orange">Automation pending</Text>
                            {po.pendingEffects.map((effect) => (
                                <Text key={effect} size="sm">
                                    {effect.replaceAll("_", " ")}
                                    {po.effectErrors?.[effect] && <Text span c="red" size="xs"> — {po.effectErrors[effect]}</Text>}
                                </Text>
                            ))}
                        </Stack>
                        {user.isDesk && user.roles.some((r) => ["dealer", "backoffice", "supervisor", "admin"].includes(r)) && (
                            <Button size="xs" variant="light" color="orange" loading={retrying} onClick={async () => {
                                setRetrying(true);
                                try {
                                    const r = await retryEffects({ poId: id! });
                                    notifications.show({ color: r.data.failed.length ? "orange" : "green", message: r.data.failed.length ? `Still failing: ${r.data.failed.join(", ")}` : "Automation completed" });
                                } catch (e) {
                                    notifications.show({ color: "red", message: (e as Error).message });
                                } finally {
                                    setRetrying(false);
                                }
                            }}>Retry automation</Button>
                        )}
                    </Group>
                </Card>
            ) : null}

            {(po.lane === "beneficiary" || pendingForThisPo) && (
                <Card withBorder mb="md" style={{ borderColor: "var(--mantine-color-green-5)" }}>
                    <Text fw={700} c="green" mb={4}>Beneficiary decision (WhatsApp and email)</Text>
                    {pendingForThisPo?.appliedAt ? (
                        <Stack gap={2}>
                            <Text size="sm">Client answered <b>{pendingForThisPo.appliedDecision}</b> {when(pendingForThisPo.appliedAt)} {pendingForThisPo.decisionChannel === "email" ? "by email" : "on WhatsApp"}.</Text>
                            <Text size="sm" c="dimmed">A change can be requested {pendingForThisPo.decisionChannel === "email" ? "by phone, or on WhatsApp" : ""} using the order reference and question code before processing starts, until {when(pendingForThisPo.changeableUntil)}.</Text>
                            {pendingForThisPo.changeRequested && <Text size="sm">Change requested{pendingForThisPo.requestedDecision ? ` → ${pendingForThisPo.requestedDecision}` : ""}{pendingForThisPo.changeError ? <Text span c="red"> — refused: {pendingForThisPo.changeError}</Text> : " (applying…)"}</Text>}
                        </Stack>
                    ) : pendingForThisPo ? (
                        <Stack gap={2}>
                            <Text size="sm">Question: <b>{pendingForThisPo.kind.replaceAll("_", " ")}</b> sent {when(pendingForThisPo.sentAt)} to {pendingForThisPo.phone}{po.whatsappPrompt?.emailTo ? ` and ${po.whatsappPrompt.emailTo}` : ""}</Text>
                            <Text size="sm">Reminder: {pendingForThisPo.reminded ? "sent" : pendingForThisPo.remindAt ? `due ${when(pendingForThisPo.remindAt)}` : "none (sent too late in the day)"} · Expires {when(pendingForThisPo.expiresAt)} (end of the day, Kuwait)</Text>
                            {pendingForThisPo.lastInboundBody && <Text size="sm" c="dimmed">Last reply not understood ({pendingForThisPo.repromptCount ?? 0}×): “{pendingForThisPo.lastInboundBody}”</Text>}
                            {pendingForThisPo.decision && <Text size="sm">Answer received{pendingForThisPo.decisionChannel === "email" ? " by email" : ""}: <b>{pendingForThisPo.decision}</b>{pendingForThisPo.decisionError ? <Text span c="red"> — could not be applied: {pendingForThisPo.decisionError}</Text> : " (applying…)"}</Text>}
                        </Stack>
                    ) : (
                        <Text size="sm" c="dimmed">No open beneficiary question (WhatsApp / email) for this order{po.whatsappPrompt ? ` (last prompt ${when(po.whatsappPrompt.sentAt)})` : ""}. If the client answered by phone, record the decision with the buttons above.</Text>
                    )}
                </Card>
            )}

            {user.isDesk && <CtcDeliveryRecovery poId={id!} />}
            {/* Same layout as the fund order page: expandable sections of info cards */}
            <Accordion multiple variant="contained" value={openSections} onChange={setOpenSections}>
                <Accordion.Item value="details">
                    <Accordion.Control><SectionTitle icon={<IconFileDescription size={28} />} title="Order details" /></Accordion.Control>
                    <Accordion.Panel>
                        {/* Timeline in its own column, so its height doesn't stretch the cards beside it */}
                        <Grid gap="md" align="flex-start">
                            <Grid.Col span={{ base: 12, lg: 8 }}>
                                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                                    <CtcInfoCard title="Order Information" icon={<IconBuildingBank size={18} />} color="green" rows={[
                                        info("Amount", formatMoney(po.amount, po.currencyId)),
                                        // Name, not the id (soft-deleted profiles still resolve: the document is kept)
                                        info("Commission profile", (() => {
                                            const c = commissions?.find((x) => x.id === po.commissionId);
                                            return c ? [c.name_en, c.name_ar].filter(Boolean).join(" · ") : po.commissionId;
                                        })()),
                                        info("Client reference", po.clientRefNo),
                                        info("Value date", formatValueDate(po.valueDate)),
                                        // The customer's dynamic fields, with the labels saved on the order
                                        ...ctcDynamicFieldRows(po, "en", currentFieldLabels).map(([label, value]) => info(label, value)),
                                        info("Maker", po.makerEmail),
                                        info("Checker", po.checkerEmail),
                                        info("Accepted by", po.acceptedByEmail),
                                        info("Dealer", po.assignedDealerEmail),
                                        info("Rejection reason", po.rejectionReason),
                                        info("Cancel reason", po.cancelReason ? `${po.cancelReason}${po.cancelledByEmail ? ` (by ${po.cancelledByEmail})` : ""}` : undefined),
                                    ]} />
                                    <CtcInfoCard title="Beneficiary Information" icon={<IconUser size={18} />} color="orange" rows={[
                                        info("Number", po.beneficiary?.number),
                                        info("WhatsApp", po.beneficiary?.whatsapp),
                                        info("Email", po.beneficiary?.email),
                                    ]} />
                                    <CtcInfoCard title="Purchase Information" icon={<IconBox size={18} />} color="blue" rows={[
                                        info("Broker", brokers?.find((b) => b.id === po.brokerId)?.name_en ?? po.brokerId),
                                        info("Commodity", commodities?.find((c) => c.id === po.commodityId)?.name_en ?? po.commodityId),
                                        info("Quantity", po.quantity),
                                        info("Unit price", po.unitPrice),
                                        info("Location", po.locationName),
                                        info("Certificate", po.certificateNumber),
                                    ]} />
                                    <CtcInfoCard title="Documents" icon={<IconFiles size={18} />} color="cyan" rows={[
                                        ...(po.attachments ?? []).map((a) => ({
                                            label: a.label ?? `Attachment No. ${a.number}`,
                                            sublabel: a.source === "upload" ? a.fileName : undefined, // uploaded file: its name on a second line
                                            value: <Anchor size="sm" onClick={async () => {
                                                try { window.open(await getDownloadURL(storageRef(storage, a.path)), "_blank"); } catch (e) { notifications.show({ color: "red", message: (e as Error).message }); }
                                            }}>{a.source === "upload" ? "Open file" : "Open PDF"}</Anchor>,
                                        })),
                                        ...(!po.attachments?.length ? [info("Documents", "No documents generated yet")] : []),
                                        ...(po.whatsappCopySent?.["4"] ? [info("WhatsApp", "Transfer confirmation sent")] : []),
                                        ...(po.whatsappCopySent?.["5"] ? [info("WhatsApp", "Sale confirmation sent")] : []),
                                    ]} />
                                    <CtcInfoCard title="Commissions Details" icon={<IconReceipt size={18} />} color="teal" rows={[
                                        info("Customer commission", po.customerCommission !== undefined ? formatMoney(po.customerCommission, po.commissionCurrency?.customer) : "Not calculated yet"),
                                        // Bank makers / checkers don't see the broker commission or its settlement
                                        ...(hideBroker ? [] : [info("Broker commission", po.brokerCommission !== undefined ? formatMoney(po.brokerCommission, po.commissionCurrency?.broker) : "Not calculated yet")]),
                                        ...(settlement?.deleted ?
                                            [info("Settlement", "Not due: order cancelled")] :
                                            [settlementRow("Customer settlement", settlement?.customer), ...(hideBroker ? [] : [settlementRow("Broker settlement", settlement?.broker)])]),
                                    ]} />
                                </SimpleGrid>
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, lg: 4 }}>
                                <OrderTimeline po={po} />
                            </Grid.Col>
                        </Grid>
                    </Accordion.Panel>
                </Accordion.Item>
                {user.isDesk && (
                    <Accordion.Item value="evidence">
                        <Accordion.Control><SectionTitle icon={<IconMessageCircle size={28} />} title="Client instructions and evidence" /></Accordion.Control>
                        <Accordion.Panel><CtcDecisionEvidence poId={id!} bare /></Accordion.Panel>
                    </Accordion.Item>
                )}
                <Accordion.Item value="history">
                    <Accordion.Control><SectionTitle icon={<IconHistory size={28} />} title="History" /></Accordion.Control>
                    <Accordion.Panel><CtcHistory poId={id!} /></Accordion.Panel>
                </Accordion.Item>
            </Accordion>

            <Modal opened={!!pending} onClose={() => !busy && closeModal()} title={pending ? CTC_ACTION_LABELS[pending].label : ""}>
                <Stack>
                    {needsReason && <Textarea label={pending?.startsWith("beneficiary_") ? "How was the client instruction verified? Include the contact reference." : "Reason"} required value={reason} onChange={(e) => setReason(e.currentTarget.value)} />}
                    {needsDealer && <Select label="Dealer" description="Staff in the dealer groups (Back office → Dealers)" required searchable nothingFoundMessage="No dealer matches" placeholder="Search by name or email" data={dealerOptions} value={dealerEmail || null} onChange={(v) => setDealerEmail(v ?? "")} />}
                    {needsPurchase && (
                        <>
                            <Select label="Broker" required searchable data={(brokers ?? []).filter(isCtcActive).map((b) => ({ value: b.id!, label: b.name_en }))} value={purchase.brokerId} onChange={pickBroker} />
                            <Select
                                label="Location"
                                required
                                searchable
                                disabled={!purchase.brokerId}
                                placeholder={!purchase.brokerId ? "Choose a broker first" : brokerLocations.length ? "Choose a location" : "No locations for this broker"}
                                description={purchase.brokerId && !brokerLocations.length ? "Add locations in Back office → Brokers" : undefined}
                                data={brokerLocations.map(({ value, label }) => ({ value, label }))}
                                value={purchase.locationId}
                                onChange={(v) => setPurchase({ ...purchase, locationId: v, locationName: brokerLocations.find((l) => l.value === v)?.name ?? "" })}
                            />
                            <Select label="Commodity" required searchable data={(commodities ?? []).filter(isCtcActive).map((c) => ({ value: c.id!, label: `${c.name_en} (${c.measurementUnit})` }))} value={purchase.commodityId} onChange={pickCommodity} />
                            <NumberInput
                                label="Unit price"
                                required
                                min={0}
                                decimalScale={12}
                                thousandSeparator=","
                                description={selectedCommodity?.price ? `Indicative price ${formatMoney(selectedCommodity.price, selectedCommodity.currencyId)}. Changing it recalculates the quantity.` : undefined}
                                value={purchase.unitPrice}
                                onChange={(v) => setPurchase({ ...purchase, unitPrice: v, quantity: quantityFor(v) })}
                            />
                            <NumberInput
                                label={`Quantity${selectedCommodity ? ` (${selectedCommodity.measurementUnit})` : ""}`}
                                required
                                min={0}
                                decimalScale={12}
                                thousandSeparator=","
                                description={`Order amount ${formatMoney(po.amount, po.currencyId)} ÷ unit price. You can edit it.`}
                                value={purchase.quantity}
                                onChange={(v) => setPurchase({ ...purchase, quantity: v })}
                            />
                            {purchaseTotal > 0 && (
                                <Text size="xs" c={Math.abs(purchaseTotal - po.amount) > 1 ? "orange" : "dimmed"}>
                                    Quantity × unit price = {formatMoney(purchaseTotal, po.currencyId)} (order amount {formatMoney(po.amount, po.currencyId)})
                                </Text>
                            )}
                            {commodityCurrencyMismatch && (
                                <Text size="xs" c="orange">
                                    The commodity is priced in {selectedCommodity?.currencyId} but the order is in {po.currencyId}. Check the unit price before confirming.
                                </Text>
                            )}
                            <TextInput label="Certificate number" required value={purchase.certificateNumber} onChange={(e) => setPurchase({ ...purchase, certificateNumber: e.currentTarget.value })} />
                        </>
                    )}
                    {!needsReason && !needsDealer && !needsPurchase && !uploadStep && <Text size="sm">Confirm <b>{pending && CTC_ACTION_LABELS[pending].label}</b> on {po.referenceNumber}?</Text>}
                    {uploadStep?.documents.map((d) => (
                        <CtcFilePicker
                            key={d.kind}
                            label={d.label}
                            required={d.required}
                            files={documentFiles[d.kind] ?? []}
                            maxFiles={CTC_MAX_DEALER_UPLOAD_FILES}
                            disabled={busy}
                            onChange={(files) => setDocumentFiles((prev) => ({ ...prev, [d.kind]: files }))}
                        />
                    ))}
                    <Group justify="flex-end">
                        <Button variant="default" disabled={busy} onClick={closeModal}>Cancel</Button>
                        <Button loading={busy} color={pending ? CTC_ACTION_LABELS[pending].color : undefined} onClick={run} disabled={(needsReason && !reason.trim()) || (needsDealer && !dealerEmail.includes("@")) || (needsPurchase && (!purchase.locationName.trim() || !purchase.certificateNumber.trim() || !(Number(purchase.quantity) > 0) || !(Number(purchase.unitPrice) > 0))) || missingUpload}>Confirm</Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};

export default CtcOrder;
